<template>
  <div class="user-engagement-table">
    <!-- Table Header -->
    <div class="table-toolbar">
      <h3 class="table-title">
        <i class="fas fa-users"></i>
        Learner Engagement Table
        <span class="total-badge" v-if="!loading">{{ total.toLocaleString() }} users</span>
      </h3>

      <div class="toolbar-controls">
        <!-- Search -->
        <div class="search-wrap">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            class="search-input"
            placeholder="Search by name or email…"
            @input="debouncedSearch"
          />
        </div>

        <!-- Segment filter -->
        <select v-model="segmentFilter" class="filter-select" @change="loadUsers(true)">
          <option value="">All Segments</option>
          <option v-for="s in SEGMENTS" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>

        <!-- At-risk toggle -->
        <button
          class="filter-btn"
          :class="{ active: atRiskFilter }"
          @click="toggleAtRisk"
        >
          <i class="fas fa-exclamation-circle"></i>
          At-Risk Only
        </button>

        <!-- Sort -->
        <select v-model="sortBy" class="filter-select" @change="loadUsers(true)">
          <option value="-rfm_total">Engagement Score ↓</option>
          <option value="rfm_total">Engagement Score ↑</option>
          <option value="-streak_days">Streak ↓</option>
          <option value="days_since_last_login">Last Active (recent first)</option>
          <option value="-days_since_last_login">Last Active (oldest first)</option>
          <option value="-total_watch_time_seconds">Watch Time ↓</option>
        </select>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="table-loading">
      <div v-for="n in 8" :key="n" class="skeleton-row"></div>
    </div>

    <!-- Empty state -->
    <div v-else-if="users.length === 0" class="table-empty">
      <i class="fas fa-user-slash"></i>
      <p>No users match your filters.</p>
      <button class="btn-clear" @click="clearFilters">Clear filters</button>
    </div>

    <!-- Table -->
    <div v-else class="table-wrap">
      <table class="eng-table">
        <thead>
          <tr>
            <th class="col-user">Learner</th>
            <th class="col-segment">Segment</th>
            <th class="col-score">Score (R/F/M)</th>
            <th class="col-streak">Streak</th>
            <th class="col-last">Last Active</th>
            <th class="col-courses">Courses</th>
            <th class="col-watch">Watch Time</th>
            <th class="col-sessions">Sessions/30d</th>
            <th class="col-action"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.user_id"
            class="user-row"
            :class="{ 'row--at-risk': user.at_risk }"
            @click="$emit('user-click', user)"
          >
            <!-- Learner -->
            <td class="col-user">
              <div class="user-cell">
                <div class="user-avatar" :class="avatarClass(user)">
                  {{ userInitials(user) }}
                </div>
                <div class="user-info">
                  <div class="user-name">{{ user.full_name || '—' }}</div>
                  <div class="user-email">{{ user.email }}</div>
                </div>
                <i v-if="user.at_risk" class="fas fa-exclamation-circle risk-icon" title="At risk"></i>
              </div>
            </td>

            <!-- Segment -->
            <td class="col-segment">
              <span class="seg-badge" :class="`seg--${(user.rfm_segment || 'casual').toLowerCase()}`">
                {{ user.rfm_segment || '—' }}
              </span>
            </td>

            <!-- Score -->
            <td class="col-score">
              <div class="rfm-score">
                <span class="rfm-total">{{ user.rfm_total || 3 }}</span>
                <span class="rfm-breakdown">
                  R{{ user.recency_score }} / F{{ user.frequency_score }} / M{{ user.momentum_score }}
                </span>
              </div>
            </td>

            <!-- Streak -->
            <td class="col-streak">
              <div class="streak-cell" v-if="user.streak_days > 0">
                <i class="fas fa-fire streak-icon" :class="streakClass(user.streak_days)"></i>
                {{ user.streak_days }}d
              </div>
              <span v-else class="text-muted">—</span>
            </td>

            <!-- Last Active -->
            <td class="col-last">
              <div :class="recencyClass(user.days_since_last_login)">
                {{ formatLastActive(user.days_since_last_login) }}
              </div>
            </td>

            <!-- Courses -->
            <td class="col-courses">
              <div class="courses-cell">
                <span class="enrolled">{{ user.courses_enrolled || 0 }} enrolled</span>
                <span class="completed" v-if="user.courses_completed">
                  · {{ user.courses_completed }} done
                </span>
              </div>
            </td>

            <!-- Watch Time -->
            <td class="col-watch">
              {{ formatDuration(user.total_watch_time_seconds) }}
            </td>

            <!-- Sessions -->
            <td class="col-sessions">
              <div class="sessions-cell">
                <div class="sessions-bar">
                  <div
                    class="sessions-fill"
                    :style="{ width: Math.min(user.sessions_last_30d / 12 * 100, 100) + '%' }"
                  ></div>
                </div>
                {{ user.sessions_last_30d }}
              </div>
            </td>

            <!-- Action -->
            <td class="col-action">
              <button class="btn-detail" @click.stop="$emit('user-click', user)">
                <i class="fas fa-chart-bar"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="total > pageSize" class="pagination">
      <button class="page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
        <i class="fas fa-chevron-left"></i>
      </button>
      <span class="page-info">
        Page {{ currentPage }} of {{ totalPages }} &nbsp;·&nbsp; {{ total }} users
      </span>
      <button class="page-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { debounce } from 'lodash-es'
import { UI_CONSTANTS } from '@/constants'
import api from '@/services/api'

const SEGMENTS = [
  { value: 'CHAMPION',  label: 'Champions' },
  { value: 'LOYAL',     label: 'Loyal Learners' },
  { value: 'PROMISING', label: 'Promising' },
  { value: 'NEW',       label: 'New Learners' },
  { value: 'CASUAL',    label: 'Casual' },
  { value: 'AT_RISK',   label: 'At Risk' },
  { value: 'DORMANT',   label: 'Dormant' },
]

export default {
  name: 'UserEngagementTable',

  props: {
    initialSegment: { type: String, default: null },
    initialAtRisk:  { type: Boolean, default: false },
  },

  emits: ['user-click'],

  setup(props) {
    const loading = ref(false)
    const users = ref([])
    const total = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(UI_CONSTANTS.DEFAULT_PAGE_SIZE || 25)
    const searchQuery = ref('')
    const segmentFilter = ref(props.initialSegment || '')
    const atRiskFilter = ref(props.initialAtRisk || false)
    const sortBy = ref('-rfm_total')

    const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

    const loadUsers = async (resetPage = false) => {
      if (resetPage) currentPage.value = 1
      loading.value = true
      try {
        const params = {
          limit: pageSize.value,
          offset: (currentPage.value - 1) * pageSize.value,
          order_by: sortBy.value,
        }
        if (segmentFilter.value) params.segment = segmentFilter.value
        if (atRiskFilter.value) params.at_risk = 'true'
        if (searchQuery.value) params.search = searchQuery.value

        const res = await api.get('/lms/engagement/users/', { params })
        users.value = res.data.results || []
        total.value = res.data.total || 0
      } catch (e) {
        console.error('Failed to load engagement users', e)
      } finally {
        loading.value = false
      }
    }

    const debouncedSearch = debounce(() => loadUsers(true), 300)

    const goToPage = (page) => {
      currentPage.value = page
      loadUsers(false)
    }

    const toggleAtRisk = () => {
      atRiskFilter.value = !atRiskFilter.value
      segmentFilter.value = ''
      loadUsers(true)
    }

    const clearFilters = () => {
      searchQuery.value = ''
      segmentFilter.value = ''
      atRiskFilter.value = false
      sortBy.value = '-rfm_total'
      loadUsers(true)
    }

    // Exposed for parent EngagementDashboard to call
    const applyFilter = ({ segment, at_risk }) => {
      segmentFilter.value = segment || ''
      atRiskFilter.value = Boolean(at_risk)
      loadUsers(true)
    }

    // Helpers
    const userInitials = (user) => {
      const name = user.full_name || user.email || '?'
      return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    }

    const avatarClass = (user) => {
      const seg = (user.rfm_segment || 'casual').toLowerCase()
      return `avatar--${seg}`
    }

    const recencyClass = (days) => {
      if (days <= 3)  return 'recency recency--hot'
      if (days <= 7)  return 'recency recency--warm'
      if (days <= 14) return 'recency recency--cool'
      if (days <= 30) return 'recency recency--cold'
      return 'recency recency--dormant'
    }

    const streakClass = (days) => {
      if (days >= 14) return 'streak--gold'
      if (days >= 7)  return 'streak--orange'
      return 'streak--red'
    }

    const formatLastActive = (days) => {
      if (days === 999 || days === null || days === undefined) return 'Never'
      if (days === 0) return 'Today'
      if (days === 1) return 'Yesterday'
      if (days < 30)  return `${days}d ago`
      const weeks = Math.round(days / 7)
      if (weeks < 8)  return `${weeks}w ago`
      const months = Math.round(days / 30)
      return `${months}mo ago`
    }

    const formatDuration = (seconds) => {
      if (!seconds) return '—'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      if (h > 0) return `${h}h ${m}m`
      return `${m}m`
    }

    onMounted(() => loadUsers())

    return {
      SEGMENTS,
      loading, users, total, currentPage, pageSize, totalPages,
      searchQuery, segmentFilter, atRiskFilter, sortBy,
      loadUsers, debouncedSearch, goToPage, toggleAtRisk, clearFilters, applyFilter,
      userInitials, avatarClass, recencyClass, streakClass, formatLastActive, formatDuration,
    }
  }
}
</script>

<style scoped>
.user-engagement-table {
  background: white; border-radius: 12px; border: 1px solid #e2e8f0;
  overflow: hidden; margin-top: 1rem;
}

/* Toolbar */
.table-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid #f1f5f9; flex-wrap: wrap; gap: 0.75rem;
}
.table-title {
  font-size: 0.95rem; font-weight: 600; color: #1e293b; margin: 0;
  display: flex; align-items: center; gap: 0.5rem;
}
.table-title i { color: #7c3aed; }
.total-badge {
  background: #f1f5f9; color: #475569; font-size: 0.75rem;
  padding: 0.2rem 0.6rem; border-radius: 10px; font-weight: 500;
}

.toolbar-controls { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.search-wrap { position: relative; }
.search-icon {
  position: absolute; left: 0.6rem; top: 50%; transform: translateY(-50%);
  color: #94a3b8; font-size: 0.8rem;
}
.search-input {
  padding: 0.4rem 0.75rem 0.4rem 2rem; border: 1px solid #e2e8f0;
  border-radius: 8px; font-size: 0.8rem; width: 200px;
  outline: none; color: #374151;
}
.search-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 2px rgba(124,58,237,0.1); }

.filter-select {
  padding: 0.4rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 0.8rem; color: #374151; background: white; cursor: pointer;
  outline: none;
}
.filter-select:focus { border-color: #7c3aed; }

.filter-btn {
  display: flex; align-items: center; gap: 0.35rem;
  padding: 0.4rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 0.8rem; font-weight: 500; background: white; color: #374151;
  cursor: pointer; transition: all 0.15s;
}
.filter-btn:hover { border-color: #ea580c; color: #ea580c; }
.filter-btn.active { background: #fff7ed; border-color: #ea580c; color: #ea580c; }

/* Skeleton */
.table-loading { padding: 0.75rem 1.25rem; display: flex; flex-direction: column; gap: 0.5rem; }
.skeleton-row {
  height: 44px; background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.table-empty {
  text-align: center; padding: 3rem; color: #94a3b8;
}
.table-empty i { font-size: 2rem; margin-bottom: 0.5rem; display: block; }
.btn-clear {
  margin-top: 0.75rem; padding: 0.5rem 1rem; background: #7c3aed; color: white;
  border: none; border-radius: 8px; font-size: 0.8rem; cursor: pointer;
}

/* Table */
.table-wrap { overflow-x: auto; }
.eng-table {
  width: 100%; border-collapse: collapse;
}
.eng-table th {
  padding: 0.6rem 0.875rem; text-align: left; font-size: 0.72rem; font-weight: 600;
  color: #64748b; text-transform: uppercase; letter-spacing: 0.04em;
  border-bottom: 1px solid #f1f5f9; background: #fafafa; white-space: nowrap;
}
.eng-table td {
  padding: 0.7rem 0.875rem; vertical-align: middle;
  border-bottom: 1px solid #f8fafc;
}

.user-row { cursor: pointer; transition: background 0.1s; }
.user-row:hover { background: #faf5ff; }
.user-row:last-child td { border-bottom: none; }
.row--at-risk td:first-child { border-left: 3px solid #ea580c; }

/* User cell */
.user-cell { display: flex; align-items: center; gap: 0.6rem; }
.user-avatar {
  width: 34px; height: 34px; border-radius: 50%; display: flex;
  align-items: center; justify-content: center; font-size: 0.7rem;
  font-weight: 700; color: white; flex-shrink: 0;
}
.avatar--champion  { background: #7c3aed; }
.avatar--loyal     { background: #2563eb; }
.avatar--promising { background: #0891b2; }
.avatar--new       { background: #16a34a; }
.avatar--casual    { background: #6b7280; }
.avatar--at_risk   { background: #ea580c; }
.avatar--dormant   { background: #dc2626; }

.user-name { font-size: 0.82rem; font-weight: 600; color: #1e293b; }
.user-email { font-size: 0.72rem; color: #94a3b8; }
.risk-icon { color: #ea580c; font-size: 0.8rem; margin-left: auto; }

/* Segment badge */
.seg-badge {
  font-size: 0.65rem; font-weight: 700; padding: 0.2rem 0.5rem;
  border-radius: 10px; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap;
}
.seg--champion  { background: #ede9fe; color: #5b21b6; }
.seg--loyal     { background: #dbeafe; color: #1d4ed8; }
.seg--promising { background: #cffafe; color: #0e7490; }
.seg--new       { background: #dcfce7; color: #166534; }
.seg--casual    { background: #f1f5f9; color: #475569; }
.seg--at_risk   { background: #ffedd5; color: #c2410c; }
.seg--dormant   { background: #fee2e2; color: #b91c1c; }

/* RFM Score */
.rfm-score { display: flex; flex-direction: column; gap: 0.15rem; }
.rfm-total { font-size: 1rem; font-weight: 700; color: #7c3aed; }
.rfm-breakdown { font-size: 0.68rem; color: #94a3b8; }

/* Streak */
.streak-cell { display: flex; align-items: center; gap: 0.3rem; font-size: 0.82rem; font-weight: 600; }
.streak-icon { font-size: 0.85rem; }
.streak--gold   { color: #d97706; }
.streak--orange { color: #ea580c; }
.streak--red    { color: #dc2626; }
.text-muted { color: #d1d5db; }

/* Recency */
.recency { font-size: 0.8rem; font-weight: 500; }
.recency--hot     { color: #16a34a; }
.recency--warm    { color: #65a30d; }
.recency--cool    { color: #0891b2; }
.recency--cold    { color: #d97706; }
.recency--dormant { color: #dc2626; }

/* Courses */
.courses-cell { font-size: 0.8rem; color: #374151; }
.completed { color: #16a34a; }

/* Sessions bar */
.sessions-cell { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; }
.sessions-bar { width: 50px; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.sessions-fill { height: 100%; background: #7c3aed; border-radius: 3px; }

/* Action button */
.btn-detail {
  padding: 0.35rem 0.6rem; background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 6px; color: #7c3aed; cursor: pointer; font-size: 0.85rem;
  transition: all 0.1s;
}
.btn-detail:hover { background: #ede9fe; border-color: #7c3aed; }

/* Pagination */
.pagination {
  display: flex; align-items: center; justify-content: center; gap: 1rem;
  padding: 0.875rem; border-top: 1px solid #f1f5f9;
}
.page-btn {
  width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e2e8f0;
  background: white; color: #374151; cursor: pointer; display: flex;
  align-items: center; justify-content: center; transition: all 0.1s;
}
.page-btn:hover:not(:disabled) { border-color: #7c3aed; color: #7c3aed; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8rem; color: #64748b; }
</style>
